import { Injectable, inject, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpStatusCode
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth-new.service';
import { environment } from '@environments/environment';

@Injectable()
export class ApiUnauthorisedInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);

  // Lazy-resolve AuthService to break circular DI:
  // AuthService → HttpClient → HTTP_INTERCEPTORS → this interceptor → AuthService
  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  // Matches the header AuthInterceptor stamps on token-refresh retry requests
  private static readonly RETRY_HEADER = 'X-Token-Refresh-Retry';

  // Flag to prevent multiple logout calls when multiple API requests fail simultaneously
  private isLoggingOut = false;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip interception for auth/token endpoints to avoid interference
    const currentCompany = localStorage.getItem('currentCompany') || 'unity';
    const baseUrl = currentCompany === 'mingle' ? environment.Prepaid_Mingle_API : environment.BASE_API;

    const skipUrls = [
      `${baseUrl}api/identity/login`,
      `${baseUrl}api/identity/refresh-token`,
      `${baseUrl}api/identity/validate-token`,
      'api/CAS/tokenRefresh',
      'api/CAS/signout',
      'api/CAS/login'
    ];

    if (skipUrls.some(url => request.url.includes(url))) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldLogout(error, request) && !this.isLoggingOut) {
          this.isLoggingOut = true;

          console.warn('[ApiUnauthorisedInterceptor] Auth failure after token refresh attempt. Logging out...');

          this.authService.logout('/auth/login').catch(err => {
            console.error('[ApiUnauthorisedInterceptor] Error during logout:', err);
          }).finally(() => {
            setTimeout(() => {
              this.isLoggingOut = false;
            }, 5000);
          });
        }
        return throwError(() => error);
      })
    );
  }

  private shouldLogout(error: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;

    if (error.status === HttpStatusCode.Unauthorized) {
      // For 401: only logout after AuthInterceptor has already attempted a token refresh.
      // AuthInterceptor stamps X-Token-Refresh-Retry on the retry request, so its presence
      // means refresh was attempted and the server still rejected — safe to logout now.
      return request.headers.has(ApiUnauthorisedInterceptor.RETRY_HEADER);
    }

    // For 403 Forbidden: no token refresh will help, logout immediately.
    return error.status === HttpStatusCode.Forbidden;
  }
} 
